import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag } from 'lucide-react';
import { Button } from './ui/button';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

export const ProductCard = ({ product, featured = false }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const { addToCart } = useCart();

  const handleQuickAdd = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!product.variants || product.variants.length === 0) {
      toast.error('Product not available');
      return;
    }

    // Get first available variant
    const availableVariant = product.variants.find(v => v.available) || product.variants[0];
    
    setIsAdding(true);
    try {
      await addToCart(availableVariant.id, 1);
      toast.success(`${product.title} added to cart!`);
    } catch (err) {
      toast.error('Failed to add to cart');
    } finally {
      setIsAdding(false);
    }
  };

  const price = parseFloat(product.price || 34.99).toFixed(2);

  return (
    <Link
      to={`/product/${product.handle}`}
      className="group block"
      data-testid={`product-card-${product.handle}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="relative overflow-hidden bg-wild-khaki/20 aspect-square mb-4">
        {/* Featured Badge */}
        {featured && (
          <div className="absolute top-4 left-4 z-10 bg-wild-leather text-white px-3 py-1 font-body text-xs tracking-wider">
            Featured
          </div>
        )}

        {/* Product Image */}
        <img
          src={product.image || '/placeholder-product.jpg'}
          alt={product.title}
          className={`w-full h-full object-cover transition-transform duration-500 ${
            isHovered ? 'scale-110' : 'scale-100'
          }`}
          loading="lazy"
        />

        {/* Quick Add Button */}
        <div
          className={`absolute bottom-0 left-0 right-0 p-4 transition-all duration-300 ${
            isHovered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <Button
            onClick={handleQuickAdd}
            disabled={isAdding}
            className="w-full bg-wild-dark text-white hover:bg-wild-leather font-headings tracking-wider"
            data-testid={`quick-add-${product.handle}`}
          >
            <ShoppingBag className="h-4 w-4 mr-2" />
            {isAdding ? 'ADDING...' : 'QUICK ADD'}
          </Button>
        </div>
      </div>

      {/* Product Info */}
      <div className="text-center">
        {/* Title */}
        <h3 className="font-headings text-lg tracking-wider text-wild-dark group-hover:text-wild-leather transition-colors mb-1">
          {product.title}
        </h3>

        {/* Price */}
        <p className="font-body text-lg font-semibold text-wild-dark">
          ${price}
        </p>
      </div>
    </Link>
  );
};
